import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {ContactosService} from './contactos.service';
import {ErrorService} from '../../../../../shared/services/error.service';
import {Contacto, ContactoFiltros} from './contactos.interface';
import {MatIcon} from '@angular/material/icon';
import {DatePipe} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import {DeleteTrigger} from '../../../../../shared/components/ui/delete-trigger/delete-trigger';
import {SnackbarService} from '@shared/services/snackbar.service';
import {DialogService} from '@shared/services/dialog.service';
import {CrearContacto} from './crear-contacto/crear-contacto';
import {DetalleContacto} from './detalle-contacto/detalle-contacto';
import {ContactosServicios} from '../contactos-servicios/contactos-servicios';
import {PageHeaderComponent} from '../../../../../shared/components/common/page-header/page-header.component';
import {CombosService} from '../../../../../shared/services/combos.service';
import {PropuestasComercialesService} from '../propuestas-comerciales/propuestas-comerciales.service';
import {
  Pagination,
  FilterSelect,
  FilterDate,
  FilterOption,
  DataTable,
  TableColumn,
  TableCellDirective,
  FiltersContainer
} from '../../../../../shared/components/ui/list-components';

@Component({
  selector: 'app-contactos',
  imports: [
    HasPermissionDirective,
    MatIcon,
    DatePipe,
    MatTooltip,
    DeleteTrigger,
    PageHeaderComponent,
    Pagination,
    FilterSelect,
    FilterDate,
    DataTable,
    TableCellDirective,
    FiltersContainer,
  ],
  templateUrl: './contactos.html'
})
export class Contactos implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  @Output() onGenerarPropuesta = new EventEmitter<number>();
  @Output() onSelect = new EventEmitter<number>();
  @Input() idProspecto!: number;
  @Input() selectionMode = false;
  @Input() showCreateButton = true;
  contactos: Contacto[] = [];
  loading = false;
  deleting = false;
  deletingIndex: number | null = null;
  downloading = false;
  downloadingIndex: number | null = null;

  // Paginación
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  // Filtros
  filtros: ContactoFiltros = {
    param_canal_contacto_id: '',
    param_motivo_contacto_id: '',
    crm_campanha_id: '',
    fecha_desde: '',
    fecha_hasta: '',
    tiene_presupuesto_enviado: ''
  };

  // Columnas de la tabla
  columns: TableColumn[] = [
    { key: 'motivo', header: 'Motivo' },
    { key: 'campanha', header: 'Campaña' },
    { key: 'fecha', header: 'Fecha' },
    { key: 'resultado', header: 'Resultado' },
    { key: 'estado', header: 'Estado' },
    { key: 'acciones', header: 'Acciones' }
  ];

  // Opciones de filtros
  canalesContactoOptions: FilterOption[] = [];
  motivosContactoOptions: FilterOption[] = [];
  campanhasOptions: FilterOption[] = [];
  tienePresupuestoOptions: FilterOption[] = [
    { value: 'true', label: 'Con presupuesto enviado' },
    { value: 'false', label: 'Sin presupuesto enviado' }
  ];

  private contactosService = inject(ContactosService);
  private combosService = inject(CombosService);
  private propuestasComercialesService = inject(PropuestasComercialesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    if (this.selectionMode) {
      this.columns = this.columns.filter((column) => column.key != 'acciones');
    }
    this.loadCombos();
    this.getContactos();
  }

  loadCombos(): void {
    // Cargar canales de contacto
    this.combosService.getCanalContactoCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.canalesContactoOptions = response.data.map((item) => ({
            value: item.id.toString(),
            label: item.nombre
          }));
        }
      },
      error: (error) => this.errorService.handleError(error)
    });

    // Cargar motivos de contacto
    this.combosService.getMotivoContactoCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.motivosContactoOptions = response.data.map((item) => ({
            value: item.id.toString(),
            label: item.nombre
          }));
        }
      },
      error: (error) => this.errorService.handleError(error)
    });

    // Cargar campañas
    this.combosService.getCampanhasCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.campanhasOptions = response.data.map((item) => ({
            value: item.id.toString(),
            label: item.nombre
          }));
        }
      },
      error: (error) => this.errorService.handleError(error)
    });
  }

  getContactos(page: number = 1): void {
    this.loading = true;
    const filtrosConProspecto = {
      ...this.filtros,
      crm_prospecto_id: this.idProspecto
    };

    this.contactosService.index(page, this.perPage, filtrosConProspecto).subscribe({
      next: (response) => {
        if (response.status) {
          this.contactos = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  // Handlers de filtros
  onCanalContactoChange(value: string | number): void {
    this.filtros.param_canal_contacto_id = value as string;
    this.currentPage = 1;
    this.getContactos();
  }

  onMotivoContactoChange(value: string | number): void {
    this.filtros.param_motivo_contacto_id = value as string;
    this.currentPage = 1;
    this.getContactos();
  }

  onCampanhaChange(value: string | number): void {
    this.filtros.crm_campanha_id = value as string;
    this.currentPage = 1;
    this.getContactos();
  }

  onFechaDesdeChange(value: string): void {
    this.filtros.fecha_desde = value;
    this.currentPage = 1;
    this.getContactos();
  }

  onFechaHastaChange(value: string): void {
    this.filtros.fecha_hasta = value;
    this.currentPage = 1;
    this.getContactos();
  }

  onTienePresupuestoChange(value: string | number): void {
    this.filtros.tiene_presupuesto_enviado = value as string;
    this.currentPage = 1;
    this.getContactos();
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filtros).some(valor => valor !== null && valor !== undefined && valor !== '');
  }

  clearFilters(): void {
    this.filtros = {
      param_canal_contacto_id: '',
      param_motivo_contacto_id: '',
      crm_campanha_id: '',
      fecha_desde: '',
      fecha_hasta: '',
      tiene_presupuesto_enviado: ''
    };
    this.currentPage = 1;
    this.getContactos();
  }

  // Paginación
  onPageChange(page: number): void {
    this.getContactos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getContactos(1);
  }

  onRowClick(event: { item: Contacto; index: number }): void {
    if (this.selectionMode) {
      if (event.item.propuesta_activa_id) {
        this.snackbarService.showSnackBar('Este contacto ya tiene una propuesta comercial activa', 'info');
        return;
      }
      this.onSelect.emit(event.item.id);
      return;
    }
    this.openViewModal(event.item.id);
  }

  openCreateModal(): void {
    this.dialogService.openForm(CrearContacto, {
      size: 'md',
      data: this.idProspecto
    }).afterClosed().subscribe(result => {
      if (result) {
        this.getContactos();
      }
    });
  }

  openViewModal(id: number): void {
    this.dialogService.openForm(DetalleContacto, {
      size: 'md',
      data: {
        idContacto: id,
        idProspecto: this.idProspecto
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.getContactos();
      }
    });
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.contactosService.destroy(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Contacto eliminado correctamente');
          this.getContactos();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deleting = false;
      this.deletingIndex = null;
    });
  }

  generar(idContacto: number): void {
    this.onGenerarPropuesta.emit(idContacto);
  }

  openServiciosModal(idContacto: number): void {
    this.dialogService.openForm(ContactosServicios, {
      size: 'lg',
      data: idContacto
    });
  }

  descargarPropuesta(idPropuesta: number): void {
    this.downloading = true;

    this.propuestasComercialesService.download(idPropuesta).subscribe({
      next: (response) => {
        this.snackbarService.showSnackBar('Se está preparando la descarga');
        const fileURL = URL.createObjectURL(response);
        window.open(fileURL, '_blank');
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.downloading = false;
    });
  }
}
